/**
 * Portable client inventory requests and responses.
 *
 * Version 2 is opportunity-scoped: a client may define several spaces and may
 * keep each space as one inventory or split it into neutral A/B inventories.
 * Signed-in designers publish request payloads behind a first-party opaque
 * /i/<code> capability link. The complete URL-fragment form remains the
 * offline/signed-out fallback, and version 1 sketch-scoped links remain
 * readable while outstanding links age out.
 */
import { parseLength } from '../geometry/length.js';

const clone = (value) => JSON.parse(JSON.stringify(value));
const INVENTORY_KINDS = new Set(['hang', 'shoes', 'drawers']);
const SPACE_MODES = new Set(['single', 'ab']);

function bytesToBase64(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  if (typeof btoa === 'function') return btoa(binary);
  return Buffer.from(bytes).toString('base64');
}

function base64ToBytes(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary');
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function encodeInventoryPayload(payload) {
  const json = JSON.stringify(payload);
  return bytesToBase64(new TextEncoder().encode(json)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function decodeInventoryPayload(encoded) {
  try {
    return JSON.parse(new TextDecoder().decode(base64ToBytes(String(encoded ?? ''))));
  } catch {
    throw new Error('This inventory link is invalid or incomplete.');
  }
}

function fieldsFromTypes(types) {
  if (!Array.isArray(types) || !types.length) throw new Error('Inventory fields are required.');
  const fields = types.map((type) => ({
    key: String(type?.key ?? '').trim(),
    label: String(type?.name ?? type?.label ?? '').trim(),
    kind: String(type?.kind ?? '').trim(),
    unit: String(type?.unit ?? '').trim(),
  }));
  if (fields.some((field) => !field.key || !field.label || !INVENTORY_KINDS.has(field.kind))) {
    throw new Error('Inventory fields are invalid.');
  }
  return fields;
}

function cleanOpportunity(value) {
  const client = String(value?.client ?? '').trim().slice(0, 160);
  const opportunity = String(value?.opportunity ?? '').trim().slice(0, 160);
  if (!client || !opportunity) throw new Error('Client and opportunity are required before creating an inventory request.');
  return { client, opportunity };
}

/** Builds a legacy sketch-specific request for outstanding v1 link support. */
export function createInventoryRequest({ id, sourceSketchId, types, createdAt } = {}) {
  if (!id || !sourceSketchId) throw new Error('Inventory request id, sketch, and fields are required.');
  return {
    schemaVersion: 1, kind: 'inventory_request', id, sourceSketchId,
    fields: fieldsFromTypes(types),
    createdAt: createdAt ?? new Date().toISOString(),
  };
}

/** Builds the current opportunity-scoped request used before spaces exist. */
export function createOpportunityInventoryRequest({ id, opportunity, types, createdAt } = {}) {
  if (!String(id ?? '').trim()) throw new Error('Inventory request id is required.');
  return {
    schemaVersion: 2,
    kind: 'inventory_request',
    scope: 'opportunity',
    id: String(id).trim(),
    opportunity: cleanOpportunity(opportunity),
    fields: fieldsFromTypes(types),
    createdAt: createdAt ?? new Date().toISOString(),
  };
}

function validateFields(value) {
  if (!Array.isArray(value) || !value.length || value.some((field) => !field?.key || !field.label || !INVENTORY_KINDS.has(field.kind))) {
    throw new Error('Invalid inventory request.');
  }
  return value;
}

export function validateRequest(value) {
  if (!value || value.kind !== 'inventory_request' || !value.id) throw new Error('Invalid inventory request.');
  validateFields(value.fields);
  if (value.schemaVersion === 1 && typeof value.sourceSketchId === 'string' && value.sourceSketchId.trim()) return value;
  if (value.schemaVersion === 2 && value.scope === 'opportunity') {
    if (value.opportunity != null) cleanOpportunity(value.opportunity);
    else if (!String(value.legacySourceSketchId ?? '').trim()) throw new Error('Invalid inventory request.');
    return value;
  }
  throw new Error('Invalid inventory request.');
}

/**
 * Converts a v1 request into the v2 form contract without discarding its source
 * sketch. This lets a client holding an older link complete it in the new form;
 * the response remains safely tied to that original sketch.
 */
export function normalizeInventoryRequest(value) {
  const request = validateRequest(clone(value));
  if (request.schemaVersion === 2) return request;
  return {
    schemaVersion: 2,
    kind: 'inventory_request',
    scope: 'opportunity',
    id: request.id,
    opportunity: null,
    legacySourceSketchId: request.sourceSketchId,
    fields: request.fields,
    createdAt: request.createdAt,
  };
}

function responseValues(fields, answers) {
  const byKey = new Map((answers ?? []).map((answer) => [answer?.key, answer]));
  return fields.map((field) => {
    const answer = byKey.get(field.key) ?? {};
    if (field.kind === 'shoes') {
      const groups = (Array.isArray(answer.shoeGroups) ? answer.shoeGroups : [])
        .filter((group) => ['existingPairs', 'neededPairs', 'width', 'widthUnits', 'length', 'lengthUnits']
          .some((key) => String(group?.[key] ?? '').trim()))
        .map((group, index) => {
          const count = (raw) => String(raw ?? '').trim() === '' ? null : Number(raw);
          const length = (raw) => String(raw ?? '').trim() === '' ? null
            : Number.isFinite(raw) ? raw : parseLength(String(raw));
          const parsed = {
            id: String(group.id ?? `shoe-${index + 1}`).slice(0, 80),
            label: String(group.label ?? '').trim().slice(0, 80),
            existingPairs: count(group.existingPairs),
            neededPairs: count(group.neededPairs),
            widthUnits: length(group.widthUnits ?? group.width),
            lengthUnits: length(group.lengthUnits ?? group.length),
          };
          if (!parsed.label) throw new Error('Name each footwear entry, such as Boots or High heels.');
          if ([parsed.existingPairs, parsed.neededPairs].some((number) => number !== null
            && (!Number.isInteger(number) || number < 0))) throw new Error(`${parsed.label} requires whole-number pair counts.`);
          if ([parsed.widthUnits, parsed.lengthUnits].some((number) => number !== null
            && (!Number.isFinite(number) || number <= 0))) throw new Error(`${parsed.label} requires positive width and toe-to-heel measurements.`);
          return parsed;
        });
      const statedExisting = groups.filter((group) => group.existingPairs !== null);
      const statedNeeded = groups.some((group) => group.neededPairs !== null);
      return {
        key: field.key,
        existing: statedExisting.length ? statedExisting.reduce((sum, group) => sum + group.existingPairs, 0) : null,
        needed: statedNeeded ? groups.reduce((sum, group) => sum + (group.neededPairs ?? group.existingPairs ?? 0), 0) : null,
        shoeGroups: groups,
      };
    }
    const parse = (raw) => raw === '' || raw == null ? null
      : field.kind === 'hang' ? parseLength(String(raw)) : Number(raw);
    const existing = parse(answer.existing);
    const needed = parse(answer.needed);
    if ((existing !== null && (!Number.isFinite(existing) || existing < 0))
      || (needed !== null && (!Number.isFinite(needed) || needed < 0))
      || (field.kind !== 'hang' && [existing, needed].some((number) => number !== null && !Number.isInteger(number)))) {
      throw new Error(`${field.label} requires non-negative values.`);
    }
    return { key: field.key, existing, needed };
  });
}

/** Creates a legacy v1 response without changing its request. */
export function createInventoryResponse(request, answers, { submittedAt } = {}) {
  const valid = validateRequest(clone(request));
  if (valid.schemaVersion !== 1) throw new Error('Use opportunity spaces for this inventory request.');
  return {
    schemaVersion: 1, kind: 'inventory_response', requestId: valid.id,
    sourceSketchId: valid.sourceSketchId, fields: valid.fields,
    values: responseValues(valid.fields, answers),
    submittedAt: submittedAt ?? new Date().toISOString(),
  };
}

function rawAnswers(fields, answers, idPrefix) {
  const byKey = new Map((answers ?? []).map((answer) => [answer?.key, answer]));
  return fields.map((field) => {
    const prior = byKey.get(field.key) ?? {};
    const answer = { key: field.key, existing: String(prior.existing ?? '').slice(0, 80), needed: String(prior.needed ?? '').slice(0, 80) };
    if (field.kind === 'shoes') {
      const groups = Array.isArray(prior.shoeGroups) && prior.shoeGroups.length
        ? prior.shoeGroups : [{ id: `${idPrefix}:${field.key}:1`, label: '' }];
      answer.shoeGroups = groups.slice(0, 30).map((group, index) => ({
        id: String(group?.id ?? `${idPrefix}:${field.key}:${index + 1}`).slice(0, 80),
        label: String(group?.label ?? '').slice(0, 80),
        existingPairs: String(group?.existingPairs ?? '').slice(0, 20),
        neededPairs: String(group?.neededPairs ?? '').slice(0, 20),
        width: String(group?.width ?? group?.widthUnits ?? '').slice(0, 40),
        length: String(group?.length ?? group?.lengthUnits ?? '').slice(0, 40),
      }));
    }
    return answer;
  });
}

function normalizeDraftSpace(fields, value, index) {
  const id = String(value?.id ?? `space-${index + 1}`).trim().slice(0, 80) || `space-${index + 1}`;
  const mode = SPACE_MODES.has(value?.mode) ? value.mode : 'single';
  const prior = value?.inventories ?? value?.answers ?? {};
  const inventories = mode === 'ab'
    ? { a: rawAnswers(fields, prior.a, `${id}:a`), b: rawAnswers(fields, prior.b, `${id}:b`) }
    : { space: rawAnswers(fields, prior.space ?? (Array.isArray(value?.answers) ? value.answers : null), `${id}:space`) };
  return {
    id,
    name: String(value?.name ?? '').slice(0, 120),
    mode,
    notes: String(value?.notes ?? '').slice(0, 2000),
    inventories,
  };
}

/** Creates the resumable v2 client draft, always with at least one space. */
export function createOpportunityInventoryDraft(request, spaces = [], { updatedAt, submittedAt = null } = {}) {
  const valid = normalizeInventoryRequest(request);
  const source = Array.isArray(spaces) && spaces.length ? spaces : [{ id: 'space-1', mode: 'single' }];
  return {
    schemaVersion: 2,
    kind: 'inventory_draft',
    requestId: valid.id,
    spaces: source.slice(0, 30).map((space, index) => normalizeDraftSpace(valid.fields, space, index)),
    updatedAt: updatedAt ?? new Date().toISOString(),
    submittedAt: typeof submittedAt === 'string' ? submittedAt : null,
  };
}

export function validateOpportunityInventoryDraft(request, value) {
  const valid = normalizeInventoryRequest(request);
  if (!value || value.schemaVersion !== 2 || value.kind !== 'inventory_draft' || value.requestId !== valid.id
    || !Array.isArray(value.spaces) || !value.spaces.length) throw new Error('Saved inventory draft is invalid.');
  return createOpportunityInventoryDraft(valid, value.spaces, {
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : undefined,
    submittedAt: value.submittedAt,
  });
}

/** Creates an opportunity response containing the client's own space names. */
export function createOpportunityInventoryResponse(request, spaces, { submittedAt } = {}) {
  const valid = normalizeInventoryRequest(request);
  const draft = createOpportunityInventoryDraft(valid, spaces);
  const responseSpaces = draft.spaces.map((space) => {
    const name = space.name.trim();
    if (!name) throw new Error('Name each space before creating the return link.');
    const inventories = {};
    for (const [slot, answers] of Object.entries(space.inventories)) inventories[slot] = responseValues(valid.fields, answers);
    return { id: space.id, name, mode: space.mode, notes: space.notes.trim(), inventories };
  });
  return {
    schemaVersion: 2,
    kind: 'inventory_response',
    scope: 'opportunity',
    requestId: valid.id,
    opportunity: valid.opportunity,
    legacySourceSketchId: valid.legacySourceSketchId ?? null,
    fields: valid.fields,
    spaces: responseSpaces,
    submittedAt: submittedAt ?? new Date().toISOString(),
  };
}

function validateValues(fields, values) {
  if (!Array.isArray(values)) throw new Error('Invalid inventory response value.');
  const byKey = new Map(fields.map((field) => [field.key, field]));
  for (const answer of values) {
    const field = byKey.get(answer?.key);
    if (!field || !['existing', 'needed'].every((key) => answer[key] == null
      || (Number.isFinite(answer[key]) && answer[key] >= 0 && (field.kind === 'hang' || Number.isInteger(answer[key]))))) {
      throw new Error('Invalid inventory response value.');
    }
    if (field.kind === 'shoes' && answer.shoeGroups != null && (!Array.isArray(answer.shoeGroups)
      || answer.shoeGroups.some((group) => !group || typeof group.id !== 'string' || typeof group.label !== 'string' || !group.label.trim()
        || !['existingPairs', 'neededPairs'].every((key) => group[key] == null || (Number.isInteger(group[key]) && group[key] >= 0))
        || !['widthUnits', 'lengthUnits'].every((key) => group[key] == null || (Number.isFinite(group[key]) && group[key] > 0))))) {
      throw new Error('Invalid shoe inventory response.');
    }
  }
}

export function validateResponse(value) {
  if (!value || value.kind !== 'inventory_response' || !value.requestId || !value.submittedAt) throw new Error('Invalid inventory response.');
  const fields = validateFields(value.fields);
  if (value.schemaVersion === 1 && typeof value.sourceSketchId === 'string' && value.sourceSketchId.trim()) {
    validateValues(fields, value.values);
    return value;
  }
  if (value.schemaVersion === 2 && value.scope === 'opportunity' && Array.isArray(value.spaces) && value.spaces.length) {
    if (value.opportunity != null) cleanOpportunity(value.opportunity);
    else if (!String(value.legacySourceSketchId ?? '').trim()) throw new Error('Invalid inventory response.');
    for (const space of value.spaces) {
      if (!space?.id || !String(space.name ?? '').trim() || !SPACE_MODES.has(space.mode)
        || typeof space.notes !== 'string' || !space.inventories || typeof space.inventories !== 'object') throw new Error('Invalid inventory response space.');
      const expected = space.mode === 'ab' ? ['a', 'b'] : ['space'];
      if (Object.keys(space.inventories).some((slot) => !expected.includes(slot)) || expected.some((slot) => !space.inventories[slot])) {
        throw new Error('Invalid inventory response space.');
      }
      for (const slot of expected) validateValues(fields, space.inventories[slot]);
    }
    return value;
  }
  throw new Error('Invalid inventory response.');
}

/** Namespaces a resumable draft to one request on the client's device. */
export function inventoryDraftKey(requestId) {
  if (!String(requestId ?? '').trim()) throw new Error('Inventory request id is required.');
  return `room-sketch:client-inventory-draft:${String(requestId).trim()}`;
}

// Legacy draft helpers remain exported for v1 tests and outstanding links.
export function createInventoryDraft(request, answers = [], { updatedAt, submittedAt = null } = {}) {
  const valid = validateRequest(clone(request));
  if (valid.schemaVersion !== 1) throw new Error('Use opportunity spaces for this inventory request.');
  return {
    schemaVersion: 1, kind: 'inventory_draft', requestId: valid.id,
    answers: rawAnswers(valid.fields, answers, valid.id),
    updatedAt: updatedAt ?? new Date().toISOString(),
    submittedAt: typeof submittedAt === 'string' ? submittedAt : null,
  };
}

export function validateInventoryDraft(request, value) {
  const valid = validateRequest(clone(request));
  if (valid.schemaVersion !== 1 || !value || value.schemaVersion !== 1 || value.kind !== 'inventory_draft'
    || value.requestId !== valid.id || !Array.isArray(value.answers)) throw new Error('Saved inventory draft is invalid.');
  return createInventoryDraft(valid, value.answers, {
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : undefined,
    submittedAt: value.submittedAt,
  });
}
